import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { useState, type ReactNode } from 'react';

import { ApiError } from '@/api/http-client';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { Toaster } from '@/shared/ui/sonner';

function criarQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        // Cache serve de paint instantâneo ao trocar de tela, mas o dado por
        // trás precisa ser sempre o real — outro médico pode ter mudado o
        // registro nos últimos 5min. `'always'` ignora o staleTime só pra
        // decisão de montagem: refaz a busca toda vez que a tela abre, sem
        // perder o cache para outras finalidades (ex.: dado inicial de forms).
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
        // 401 e 403 são respostas definitivas: repetir não muda o resultado e
        // só atrasa a tela de erro.
        retry: (tentativas, erro) => {
          if (erro instanceof ApiError && (erro.naoAutenticado || erro.semPermissao)) return false;
          return tentativas < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

/**
 * ⚠️ AuthProvider fica DENTRO do BrowserRouter (ver router.tsx), porque ele usa
 * `useNavigate` para reagir ao 401.
 */
export function Providers({ children }: { children: ReactNode }) {
  // useState garante um cliente por montagem — criar no módulo faria os testes
  // compartilharem cache entre casos.
  const [queryClient] = useState(criarQueryClient);

  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>{children}</AuthProvider>
        {/* Feedback de mutação (salvou, desativou, falhou). Dentro do
            ThemeProvider porque o toast segue o tema. */}
        <Toaster />
      </QueryClientProvider>
    </ThemeProvider>
  );
}
